import { signal } from '@preact/signals'
import type { Storage } from '../platform/storage'

/**
 * Несколько малышей на одном устройстве.
 *
 * Данные каждого лежат под своей приставкой: `kid.<id>.child_name`, `kid.<id>.notes_2026_09`
 * и так далее. Имена ключей внутри приставки не менялись, поэтому сторы, часы и Android
 * продолжают работать с теми же названиями — меняется только то, чьи данные им подсунуты.
 *
 * Общие ключи приставку не получают: ключ сессии, почта, адрес доставки уведомлений,
 * звук — они принадлежат человеку, а не малышу.
 */

/** Ключи, общие для всего устройства. Остальное относится к текущему малышу. */
/**
 * Ключи, общие для устройства: их не разделяют по малышам.
 *
 * Тот же список есть в Kotlin (`app/src/main/kotlin/app/moonwake/Keys.kt`) — настройки
 * у них одни на двоих, и разойтись здесь значит перестать видеть данные друг друга.
 * `session_token`, `child_sync_id` и `web_migrated_v1` заведены только Kotlin, но лежат
 * в том же хранилище, поэтому названы и тут.
 */
const SHARED = new Set([
  'session', 'session_token', 'email', 'device_token', 'api_base',
  'child_id', 'child_sync_id',
  'sound', 'kids', 'kid_current', 'tour_seen', 'unnamed_schedule', 'web_migrated_v1',
])

const KIDS_KEY = 'kids'
const CURRENT_KEY = 'kid_current'
/**
 * Метка «на сервере осталось безымянное расписание».
 *
 * До переезда на имена расписание устройства лежало без ключа малыша, и его задания
 * живут своей жизнью. Если переезд застал идущее окно, человек получит два одинаковых
 * уведомления об одном ребёнке — от старой записи и от новой. Убрать её должен клиент:
 * серверу решать нельзя, за тем же устройством может стоять клиент постарше, который
 * имён не присылает (тест «старый клиент без номера малыша работает по-прежнему»).
 */
export const UNNAMED_KEY = 'unnamed_schedule'

export interface Kid {
  /**
   * Номер малыша на этом устройстве, он же имя его ящика и `clientId` в посылках.
   * Не меняется никогда: переименование ящика было единственным местом, где данные
   * могли оказаться под одним именем, а указатель под другим (решение заказчика 28.10.2026).
   */
  id: string
  name: string
  /**
   * Как этого же малыша зовёт сервер, если иначе. Такое бывает только у малышей, заведённых
   * до единого номера: новым сервер своего имени не придумывает. Нужен, чтобы узнать своего
   * ребёнка в ответе, который пришёл под серверным именем, и не завести его второй раз.
   */
  serverId?: string
}

export const kids = signal<Kid[]>([])
export const currentKid = signal<string | null>(null)

export function isShared(key: string): boolean {
  return SHARED.has(key)
}

/**
 * Хранилище, повёрнутое к одному малышу: обычные ключи уходят под его приставку,
 * общие остаются на месте. Сторам об этом знать не нужно.
 */
export class KidStorage implements Storage {
  constructor(
    private readonly inner: Storage,
    private kidId: string,
  ) {}

  /** То же устройство, но данные другого малыша. Нужно обмену: он ходит по всем. */
  forKid(id: string): KidStorage {
    return new KidStorage(this.inner, id)
  }

  /** Переключение малыша: то же хранилище начинает отвечать про другого. */
  switchTo(kidId: string): void {
    this.kidId = kidId
  }

  private full(key: string): string {
    return isShared(key) ? key : `kid.${this.kidId}.${key}`
  }

  get(key: string): Promise<string | null> {
    return this.inner.get(this.full(key))
  }

  set(key: string, value: string): Promise<void> {
    return this.inner.set(this.full(key), value)
  }

  remove(key: string): Promise<void> {
    return this.inner.remove(this.full(key))
  }

  async keys(): Promise<string[]> {
    const prefix = `kid.${this.kidId}.`
    const all = await this.inner.keys()
    return all.filter((k) => k.startsWith(prefix)).map((k) => k.slice(prefix.length)).concat(all.filter(isShared))
  }

  onChange(callback: (key: string) => void): () => void {
    return this.inner.onChange?.(callback) ?? (() => {})
  }
}

/** Все ключи малыша в общем хранилище — нужны обмену и удалению. */
export function kidPrefix(id: string): string {
  return `kid.${id}.`
}

export async function loadKids(storage: Storage): Promise<{ list: Kid[]; current: string | null }> {
  const raw = await storage.get(KIDS_KEY)
  let list: Kid[] = []
  try {
    const parsed: unknown = raw === null ? [] : JSON.parse(raw)
    if (Array.isArray(parsed)) list = parsed.filter((k): k is Kid => typeof k?.id === 'string')
  } catch {
    list = []
  }
  const current = (await storage.get(CURRENT_KEY)) ?? list[0]?.id ?? null
  return { list, current }
}

export async function saveKids(storage: Storage, list: Kid[], current: string | null): Promise<void> {
  await storage.set(KIDS_KEY, JSON.stringify(list))
  if (current !== null) await storage.set(CURRENT_KEY, current)
  kids.value = list
  currentKid.value = current
}

/**
 * Приставка хранилища, пока малыша нет. Номер ребёнку даёт только сервер (решение заказчика
 * 28.10.2026): телефон не придумывает его даже как заглушку, иначе заглушка однажды уехала
 * бы на сервер настоящим номером.
 */
export const NO_KID = 'none'

// Переезда данных старой раскладки (без приставки) больше нет: он придумывал малышу номер
// на телефоне, а таких устройств в живых не осталось (ответ заказчика 28.10.2026).
